import React, { useEffect, useState, useRef } from 'react';
import { ScratchConfiguration } from '@luckycart/xpn-types';

import {
  ScratchBorder,
  ScratchBackground,
  ScratchContainer,
  VideoContainer,
  WinVideoElement,
  LoseVideoElement,
  ScratchPatternImage,
  ScratchBorderPatternLeft,
  ScratchBorderPatternRight,
  StarsContainerTop,
  StarsContainerBottom,
  StarImage,
  LoadingText,
} from './ScratchPlugin.style';
import { BlockInfoType } from '../../common-types/BlockInfo.type';
import ScratchPatternSource from './images/scratch/scratch.jpg';
import WinVideoWebm from './videos/scratch/win.webm';
import WinVideoMp4 from './videos/scratch/win.mp4';
import LoseVideoWebm from './videos/scratch/lose.webm';
import LoseVideoMp4 from './videos/scratch/lose.mp4';
import { ALT_ATTRIBUTE_STATIC_MESSAGE } from '../../utils/AltAttributeStaticMessage';

const delaiAfterVideoEnds = 1500;
const delaiBeforeLoaderDisplay = 1000;
const starsCount = 14;
const starsByLine = 7;
const stars = Array.from(Array(starsCount).keys());

interface Props {
  config: ScratchConfiguration;
  onPluginDone?: () => void;
  hasWon: null | boolean;
  addEditableBlock?: (block: BlockInfoType) => void;
  removeEditableBlock?: (configPath: string) => void;
  onBlockEdit?: React.MouseEventHandler<HTMLDivElement>;
  isPlaying: boolean;
  isEdition: boolean;
  onLoad?: () => void;
}

export const ScratchPlugin: React.FunctionComponent<Props> = ({
  config,
  hasWon,
  onPluginDone,
  addEditableBlock,
  removeEditableBlock,
  onBlockEdit,
  isPlaying,
  onLoad,
  isEdition,
}: Props) => {

  const [
    isLoaderVisible,
    setIsLoaderVisible,
  ] = useState<boolean>(false);

  const dataTemplateType = 'plugin';
  const dataTemplateConfig = 'screens.game.plugin';
  const displayName = 'plugin';
  const loaderTimeout = useRef<NodeJS.Timeout | null>(null);
  const winVideoRef = useRef<HTMLVideoElement>(null);
  const loseVideoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (isPlaying === true && loaderTimeout.current === null) {
      loaderTimeout.current = setTimeout(() => {
        setIsLoaderVisible(true);
      }, delaiBeforeLoaderDisplay);
    }
  }, [isPlaying]);

  useEffect(() => {
    if (loaderTimeout.current !== null) {
      clearTimeout(loaderTimeout.current);
    }
    const nextScreen = (): void => {
      setTimeout(() => {
        if (onPluginDone) {
          onPluginDone();
        }
      }, delaiAfterVideoEnds);
    };

    let refVideo = null;
    if (hasWon === true && winVideoRef.current !== null) {
      refVideo = winVideoRef.current;
    } else if (hasWon === false && loseVideoRef.current !== null) {
      refVideo = loseVideoRef.current;
    }
    if (refVideo !== null) {
      refVideo.defaultMuted = true;
      refVideo.muted = true;
      refVideo.addEventListener('ended', nextScreen);
      refVideo.play();
    }

    setIsLoaderVisible(false);
  }, [hasWon, onPluginDone]);

  return (
    <ScratchContainer
      onBlockEdit={onBlockEdit}
      dataTemplateType={dataTemplateType}
      displayName={displayName}
      dataTemplateConfig={dataTemplateConfig}
      isEdition={isEdition}
      addEditableBlock={addEditableBlock}
      removeEditableBlock={removeEditableBlock}
      onLoad={onLoad}
      neededAssets={[]}
    >
      <ScratchBorder>
        <ScratchBorderPatternLeft/>
        <ScratchBackground color={config.background.color}>
          <StarsContainerTop>
            {
              stars.slice(0, starsByLine).map((index) => <StarImage key={index} color={config.stars.color}/>)
            }
          </StarsContainerTop>
          <VideoContainer>
            <ScratchPatternImage src={ScratchPatternSource} alt={ALT_ATTRIBUTE_STATIC_MESSAGE}/>
            <WinVideoElement hasWon={hasWon} ref={winVideoRef} playsInline muted>
              <source src={WinVideoWebm} type="video/webm"/>
              <source src={WinVideoMp4} type="video/mp4"/>
            </WinVideoElement>
            <LoseVideoElement hasWon={hasWon} ref={loseVideoRef} playsInline muted>
              <source src={LoseVideoWebm} type="video/webm"/>
              <source src={LoseVideoMp4} type="video/mp4"/>
            </LoseVideoElement>
          </VideoContainer>
          <StarsContainerBottom>
            {
              stars.slice(starsByLine).map((index) => <StarImage key={index} color={config.stars.color}/>)
            }
          </StarsContainerBottom>
        </ScratchBackground>
        <ScratchBorderPatternRight/>
      </ScratchBorder>
      {
        isLoaderVisible && hasWon === null && <LoadingText>Chargement...</LoadingText>
      }
    </ScratchContainer>
  );
};
